/** Roles that may use the web app's asset screens. `/admin` stays `role === 'admin'` only. */
export const isSignedInRole = (role: string | null | undefined): boolean =>
  role === 'user' || role === 'admin' || role === 'org-admin';

/** Roles that always work inside one active organization. */
export const isOrgScopedRole = (role: string | null | undefined): boolean => role === 'user' || role === 'org-admin';

/**
 * Roles that may download the asset-groups Excel export. Mirrors the API gate: an admin for any
 * organization, an org-admin for its active organization only.
 */
export const canExportAssetGroups = (role: string | null | undefined): boolean =>
  role === 'admin' || role === 'org-admin';

/**
 * Roles that may close a service request. Mirrors the API gate (org-admin spec D18): an admin, or an org-admin for
 * the requests of its active organization.
 */
export const canCloseServiceRequests = (role: string | null | undefined): boolean =>
  role === 'admin' || role === 'org-admin';

/**
 * Settings sections a role sees. org-admin gets everything except categories and attributes (customer-of spec D14);
 * service request reasons are managed by admins only (D19). The API enforces the writes; this hides the screens.
 */
export const settingsSectionAllowed = (section: string, role: string | null | undefined): boolean => {
  if (!isSignedInRole(role)) return false;
  if (section === 'service-request-reasons') return role === 'admin';
  if (role === 'org-admin' && (section === 'category' || section === 'attributes')) return false;
  return true;
};

/**
 * Whether the session may review unconfirmed asset groups. The API computes it (`canReviewUnconfirmed` on
 * get-session, customer-of spec §5), so the client carries no domain rule of its own.
 */
export const canReviewUnconfirmed = (session: unknown): boolean =>
  (session as { canReviewUnconfirmed?: unknown } | null | undefined)?.canReviewUnconfirmed === true;

/**
 * A field-agent with inventory access (customer-of spec §6b) uses the web app as `user`; the API admits it to every
 * user route. Applied once to the session in lib/auth/hooks.ts, so guards and menus carry no rule of their own.
 * A field-agent without the flag keeps its role and stays outside the web app.
 */
export function withWebRole<T extends { user: { role?: string | null } } | null | undefined>(session: T): T {
  const user = session?.user as { role?: string | null; inventoryAccess?: unknown } | undefined;
  if (!session || user?.role !== 'field-agent' || user.inventoryAccess !== true) return session;
  return { ...session, user: { ...session.user, role: 'user' } } as T;
}
